import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/cn";

type SecurityScanResult = {
  domain: string;
  ipAddress: string | null;
  overallScore: number;
  grade: string;
  ssl: {
    valid: boolean;
    issuer: string | null;
    expiresAt: string | null;
    daysUntilExpiry: number | null;
    protocol: string | null;
    errors: string[];
    warnings: string[];
    score: number;
  };
  headers: {
    score: number;
    headers: {
      name: string;
      present: boolean;
      value: string | null;
      status: "pass" | "warning" | "fail";
      importance: "critical" | "high" | "medium" | "low";
      description: string;
      recommendation: string | null;
    }[];
    errors: string[];
  };
  blacklist: {
    clean: boolean;
    checked: string[];
    detectedOn: string[];
    errors: string[];
  };
  cached: boolean;
  scannedAt: string;
};

type DnsProvider = {
  provider: {
    id: string;
    name: string;
    links: {
      spf?: string;
      dkim?: string;
      dmarc?: string;
      securityHeaders?: string;
      addTxtRecord?: string;
      addCnameRecord?: string;
    };
  };
  nameservers: string[];
};

export function SecurityPage() {
  const { currentOrganization } = useAuthStore();
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [scanResults, setScanResults] = useState<Map<string, SecurityScanResult>>(new Map());

  // Fetch domains
  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ["domains", currentOrganization?.id],
    queryFn: () => api.domains.list(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
  });

  // Find the domain ID for the selected domain name
  const selectedDomainRecord = domains?.find((d) => d.domain === selectedDomain);

  // Fetch DNS provider for selected domain
  const { data: dnsProvider } = useQuery<DnsProvider>({
    queryKey: ["dns-provider", selectedDomainRecord?.id],
    queryFn: () => api.domains.dnsProvider(selectedDomainRecord!.id),
    enabled: !!selectedDomainRecord?.id,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: (domain: string) => api.tools.securityScan(domain),
    onSuccess: (data) => {
      setScanResults((prev) => new Map(prev).set(data.domain, data));
    },
  });

  const scanAllDomains = async () => {
    if (!domains) return;
    for (const domain of domains.filter((d) => d.verified)) {
      await scanMutation.mutateAsync(domain.domain);
    }
  };

  const scanSingleDomain = async (domain: string) => {
    await scanMutation.mutateAsync(domain);
    setSelectedDomain(domain);
  };

  const verifiedDomains = domains?.filter((d) => d.verified) || [];
  const selectedResult = selectedDomain ? scanResults.get(selectedDomain) : null;

  const gradeColors: Record<string, string> = {
    "A+": "text-green-600 bg-green-500/10 border-green-500/20",
    A: "text-green-600 bg-green-500/10 border-green-500/20",
    B: "text-lime-600 bg-lime-500/10 border-lime-500/20",
    C: "text-amber-600 bg-amber-500/10 border-amber-500/20",
    D: "text-orange-600 bg-orange-500/10 border-orange-500/20",
    F: "text-red-600 bg-red-500/10 border-red-500/20",
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-teal">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative">
          <div className="page-header-content">
            <div className="page-header-icon">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Website Security</h1>
              <p className="text-white/80">
                Check SSL certificates, security headers, and blacklist status
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Why this matters callout */}
      <div className="card p-4 bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-800">
        <div className="flex gap-3">
          <svg className="h-5 w-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <div>
            <p className="font-medium text-teal-900 dark:text-teal-100">Why Website Security Affects Email Deliverability</p>
            <p className="text-sm text-teal-700 dark:text-teal-300 mt-1">
              Enterprise email security systems (Proofpoint, Mimecast, Barracuda) check your domain's website reputation.
              Expired SSL certificates, missing security headers, or blacklist listings can cause your emails to be flagged
              or quarantined — even with perfect DMARC, SPF, and DKIM configuration.
            </p>
          </div>
        </div>
      </div>

      {/* Domain List */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your Domains</h2>
          {verifiedDomains.length > 0 && (
            <button
              onClick={scanAllDomains}
              disabled={scanMutation.isPending}
              className="btn btn-teal"
            >
              {scanMutation.isPending ? "Scanning..." : "Scan All Domains"}
            </button>
          )}
        </div>

        {domainsLoading ? (
          <div className="p-8 text-center text-muted-foreground">Loading domains...</div>
        ) : verifiedDomains.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <p>No verified domains found.</p>
            <p className="text-sm mt-1">Add and verify a domain to scan its security.</p>
          </div>
        ) : (
          <div className="divide-y">
            {verifiedDomains.map((domain) => {
              const result = scanResults.get(domain.domain);
              return (
                <div
                  key={domain.id}
                  className={cn(
                    "flex items-center justify-between p-4 cursor-pointer transition-colors",
                    selectedDomain === domain.domain
                      ? "bg-teal-50 dark:bg-teal-950/20"
                      : "hover:bg-muted/50"
                  )}
                  onClick={() => setSelectedDomain(domain.domain)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">{domain.domain}</p>
                      {result && (
                        <p className="text-xs text-muted-foreground">
                          Scanned {new Date(result.scannedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {result ? (
                      <div className={cn("px-3 py-1 rounded-lg text-lg font-bold border", gradeColors[result.grade])}>
                        {result.grade}
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          scanSingleDomain(domain.domain);
                        }}
                        disabled={scanMutation.isPending}
                        className="btn btn-secondary btn-sm"
                      >
                        Scan
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Domain Details */}
      {selectedResult && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            Security Details for {selectedResult.domain}
          </h2>

          {/* Score Overview */}
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="card p-4 text-center">
              <p className="text-sm text-muted-foreground">Overall Score</p>
              <p className="text-3xl font-bold mt-1">{selectedResult.overallScore}</p>
              <div className={cn("inline-block px-2 py-0.5 rounded text-sm font-semibold mt-1", gradeColors[selectedResult.grade])}>
                Grade {selectedResult.grade}
              </div>
            </div>
            <div className="card p-4 text-center">
              <p className="text-sm text-muted-foreground">SSL/TLS</p>
              <p className="text-3xl font-bold mt-1">{selectedResult.ssl.score}</p>
              <p className={cn("text-sm mt-1", selectedResult.ssl.valid ? "text-green-600" : "text-red-600")}>
                {selectedResult.ssl.valid ? "Valid" : "Invalid"}
              </p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-sm text-muted-foreground">Security Headers</p>
              <p className="text-3xl font-bold mt-1">{selectedResult.headers.score}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedResult.headers.headers.filter((h) => h.status === "pass").length}/
                {selectedResult.headers.headers.length} headers
              </p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-sm text-muted-foreground">Blacklist Status</p>
              <p className={cn("text-3xl font-bold mt-1", selectedResult.blacklist.clean ? "text-green-600" : "text-red-600")}>
                {selectedResult.blacklist.clean ? "Clean" : "Listed"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedResult.blacklist.checked.length} lists checked
              </p>
            </div>
          </div>

          {/* SSL Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold">SSL/TLS Certificate</h3>
            </div>
            <div className="p-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Issuer</p>
                  <p className="font-medium">{selectedResult.ssl.issuer || "Unknown"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Protocol</p>
                  <p className="font-medium">{selectedResult.ssl.protocol || "Unknown"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expires</p>
                  <p className="font-medium">
                    {selectedResult.ssl.expiresAt
                      ? new Date(selectedResult.ssl.expiresAt).toLocaleDateString()
                      : "Unknown"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Days Until Expiry</p>
                  <p className={cn("font-medium", selectedResult.ssl.daysUntilExpiry && selectedResult.ssl.daysUntilExpiry < 30 ? "text-amber-600" : "")}>
                    {selectedResult.ssl.daysUntilExpiry ?? "Unknown"}
                  </p>
                </div>
              </div>
              {selectedResult.ssl.errors.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-red-500/10 text-red-600 text-sm">
                  {selectedResult.ssl.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              {selectedResult.ssl.warnings.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 text-amber-600 text-sm">
                  {selectedResult.ssl.warnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              )}
            </div>
          </div>

          {/* Security Headers */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold">Security Headers</h3>
            </div>

            {/* DNS Provider Help Link */}
            {dnsProvider?.provider.links.securityHeaders && selectedResult.headers.headers.some(h => h.status !== "pass") && (
              <div className="mx-4 mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Your DNS is managed by {dnsProvider.provider.name}
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      <a
                        href={dnsProvider.provider.links.securityHeaders}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        View {dnsProvider.provider.name}'s guide to adding security headers →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Provider without security headers guide */}
            {dnsProvider && !dnsProvider.provider.links.securityHeaders && dnsProvider.provider.id !== "unknown" && selectedResult.headers.headers.some(h => h.status !== "pass") && (
              <div className="mx-4 mt-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Your DNS is managed by <span className="font-medium">{dnsProvider.provider.name}</span>.
                      Security headers are typically configured on your web server or CDN, not DNS.
                      Check your hosting provider's documentation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="divide-y">
              {selectedResult.headers.headers.map((header, i) => (
                <div key={i} className="p-4 flex items-start gap-3">
                  {header.status === "pass" ? (
                    <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : header.status === "warning" ? (
                    <svg className="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{header.name}</span>
                      <span className={cn("text-xs px-1.5 py-0.5 rounded", {
                        "bg-red-500/10 text-red-600": header.importance === "critical",
                        "bg-orange-500/10 text-orange-600": header.importance === "high",
                        "bg-amber-500/10 text-amber-600": header.importance === "medium",
                        "bg-gray-500/10 text-gray-600": header.importance === "low",
                      })}>
                        {header.importance}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{header.description}</p>
                    {header.status !== "pass" && header.recommendation && (
                      <p className="text-xs text-amber-600 mt-1">{header.recommendation}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Blacklist Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold">Blacklist Status</h3>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {selectedResult.blacklist.checked.map((list, i) => {
                  const isListed = selectedResult.blacklist.detectedOn.includes(list);
                  return (
                    <span
                      key={i}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 rounded text-sm",
                        isListed
                          ? "bg-red-500/10 text-red-600"
                          : "bg-green-500/10 text-green-600"
                      )}
                    >
                      {isListed ? (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      )}
                      {list}
                    </span>
                  );
                })}
              </div>
              {selectedResult.blacklist.errors.length > 0 && (
                <div className="mt-4 text-sm text-amber-600">
                  {selectedResult.blacklist.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          </div>

          {/* Upgrade prompt for continuous monitoring */}
          {currentOrganization?.plan === "free" && (
            <div className="rounded-xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-200 dark:border-indigo-800 p-5">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">
                    Get Continuous Security Monitoring
                  </h4>
                  <p className="mt-1 text-sm text-indigo-700 dark:text-indigo-300">
                    Upgrade to automatically monitor your security posture and get alerts when issues arise. Plus unlock longer data retention and advanced analytics.
                  </p>
                  <a
                    href="/pricing"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
                  >
                    View upgrade options
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
